import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ClipboardList, Search, RotateCcw, FileSpreadsheet, Loader2, ArrowUp, ArrowDown, ArrowUpDown, RefreshCw,
  PackageOpen, Layers, Sigma, ClipboardCheck, Clock, Hourglass,
} from "lucide-react";
import { fmtDate, fmtQty } from "@/lib/dcGinReportExport";

// Pending QC Report — Reports → Pending QC Report. Read-only: one row per
// Goods Inward Note whose QC status on the Goods Inward screen is "Pending
// QC", computed live by the backend (GET /api/reports/pending-qc, see
// backend/pending_qc_report.py). Nothing is re-entered here — the page
// refetches every minute and whenever the tab regains focus, so a GIN shows
// up as soon as it's saved and drops off once Quality approves / rejects it.

const REFRESH_MS = 60_000;

export const PQC_COLUMNS = [
  { field: "gin_date", header: "GIN Date", kind: "date" },
  { field: "gin_code", header: "GIN Number" },
  { field: "source", header: "Source" },
  { field: "ref_code", header: "PO / DC Ref" },
  { field: "supplier", header: "Supplier / Vendor" },
  { field: "invoice_no", header: "Invoice No." },
  { field: "items_text", header: "Items (Received Qty)" },
  { field: "item_count", header: "Lines", kind: "number" },
  { field: "total_received_qty", header: "Total Received Qty", kind: "number" },
  { field: "age_days", header: "Pending (Days)", kind: "number" },
  { field: "status", header: "QC Status" },
  { field: "created_by", header: "Created By" },
];

const SORTABLE = new Set([
  "gin_date", "gin_code", "source", "ref_code", "supplier", "invoice_no",
  "item_count", "total_received_qty", "age_days", "created_by",
]);

const SOURCE_OPTIONS = ["Purchase Order", "Job Work DC", "Manual"];

const SUMMARY = [
  { key: "total_gins", label: "GINs Pending QC", icon: PackageOpen, tone: "text-amber-700 bg-amber-50" },
  { key: "total_lines", label: "Item Lines", icon: Layers, tone: "text-blue-700 bg-blue-50" },
  { key: "total_received_qty", label: "Total Received Qty", icon: Sigma, tone: "text-slate-700 bg-slate-100" },
  { key: "inspection_started", label: "Inspection Saved", icon: ClipboardCheck, tone: "text-emerald-700 bg-emerald-50" },
  { key: "over_2_days", label: "Pending > 2 Days", icon: Clock, tone: "text-red-700 bg-red-50" },
  { key: "oldest_age_days", label: "Oldest (Days)", icon: Hourglass, tone: "text-violet-700 bg-violet-50" },
];

const EMPTY_FILTERS = {
  date_from: "", date_to: "", gin_no: "", supplier: "", item: "", source: "all", search: "",
};

const PAGE_SIZES = [25, 50, 100, 250];

function toParams(f) {
  const p = {};
  for (const k of ["date_from", "date_to", "gin_no", "supplier", "item", "search"]) {
    if (f[k] && String(f[k]).trim()) p[k] = String(f[k]).trim();
  }
  if (f.source && f.source !== "all") p.source = f.source;
  return p;
}

function exportExcel(rows) {
  const aoa = [
    PQC_COLUMNS.map((c) => c.header),
    ...rows.map((r) => PQC_COLUMNS.map((c) => (
      c.kind === "number" ? Number(r[c.field] || 0) : c.kind === "date" ? fmtDate(r[c.field]) : r[c.field] ?? ""
    ))),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = PQC_COLUMNS.map((c) => ({ wch: c.field === "items_text" ? 48 : 16 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Pending QC");
  XLSX.writeFile(wb, `pending_qc_report_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

function SortHeader({ col, sortBy, sortDir, onSort }) {
  const numeric = col.kind === "number";
  const sortable = SORTABLE.has(col.field);
  const active = sortBy === col.field;
  const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600 whitespace-nowrap ${numeric ? "text-right" : "text-left"}`}>
      {sortable ? (
        <button type="button" onClick={() => onSort(col.field)} className={`inline-flex items-center gap-1 hover:text-slate-900 ${active ? "text-slate-900" : ""}`} data-testid={`pqc-sort-${col.field}`}>
          {col.header}
          <Icon className={`w-3 h-3 ${active ? "" : "opacity-40"}`} />
        </button>
      ) : col.header}
    </th>
  );
}

export default function PendingQcReport() {
  const [form, setForm] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [sortBy, setSortBy] = useState("gin_date");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [tick, setTick] = useState(0);

  // Keep the report in step with the Goods Inward screen.
  useEffect(() => {
    const refresh = () => setTick((t) => t + 1);
    const id = setInterval(refresh, REFRESH_MS);
    window.addEventListener("focus", refresh);
    return () => { clearInterval(id); window.removeEventListener("focus", refresh); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api.get("/reports/pending-qc", {
      params: { ...toParams(applied), sort_by: sortBy, sort_dir: sortDir, page, page_size: pageSize },
    })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(formatApiError(err?.response?.data?.detail)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [applied, sortBy, sortDir, page, pageSize, tick]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const runSearch = (e) => {
    e?.preventDefault();
    if (form.date_from && form.date_to && form.date_from > form.date_to) {
      toast.error("Date From must be on or before Date To");
      return;
    }
    setPage(1);
    setApplied({ ...form });
  };

  const reset = () => {
    setForm(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(1);
    setSortBy("gin_date");
    setSortDir("desc");
  };

  const onSort = (field) => {
    if (sortBy === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(field); setSortDir(field === "gin_date" || field === "age_days" ? "desc" : "asc"); }
    setPage(1);
  };

  const refreshNow = useCallback(() => setTick((t) => t + 1), []);

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/reports/pending-qc", {
        params: { ...toParams(applied), sort_by: sortBy, sort_dir: sortDir, page_size: 0 },
      });
      const all = res.data.rows || [];
      if (!all.length) {
        toast.error("No rows to export — adjust or clear the filters first.");
        return;
      }
      exportExcel(all);
      toast.success(`Exported ${all.length} rows to Excel`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const rows = useMemo(() => data?.rows || [], [data]);
  const total = data?.total_rows || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const suppliers = data?.suppliers || [];

  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const renderCell = (row, col) => {
    const v = row[col.field];
    switch (col.field) {
      case "gin_code":
        return (
          <Link to={`/goods-inward?gin=${encodeURIComponent(row.gin_id)}`} className="mono font-semibold text-blue-700 hover:underline" data-testid={`pqc-gin-link-${row.id}`}>
            {v}
          </Link>
        );
      case "gin_date":
        return v ? fmtDate(v) : <span className="text-slate-300">—</span>;
      case "status":
        return (
          <span className="flex items-center gap-1.5">
            <Badge className="bg-amber-50 text-amber-700 font-medium border-0 whitespace-nowrap">{v}</Badge>
            {row.inspection_started && (
              <span className="text-[10px] text-emerald-700" title={`Inspection saved by ${row.inspected_by}, not yet approved`}>inspection saved</span>
            )}
          </span>
        );
      case "age_days":
        return <span className={`font-semibold ${Number(v) > 2 ? "text-red-700" : "text-slate-700"}`}>{fmtQty(v)}</span>;
      case "items_text":
        return <span className="block max-w-sm whitespace-normal">{v || "—"}</span>;
      case "ref_code":
        return v ? <span className="mono">{v}</span> : <span className="text-slate-300">—</span>;
      default:
        if (col.kind === "number") return fmtQty(v);
        return v || <span className="text-slate-300">—</span>;
    }
  };

  return (
    <div className="space-y-5" data-testid="pending-qc-report-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Reports</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <ClipboardList className="w-7 h-7 text-blue-600" /> Pending QC Report
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Every Goods Inward Note still marked Pending QC — pulled live from the Goods Inward screen and
            refreshed automatically.
          </p>
        </div>
        <div className="text-xs text-slate-500 flex items-center gap-2">
          {data?.generated_at && <span data-testid="pqc-updated">Updated {new Date(data.generated_at).toLocaleTimeString("en-IN")}</span>}
          <Button type="button" size="sm" variant="outline" onClick={refreshNow} disabled={loading} data-testid="pqc-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={runSearch} className="bg-white border border-slate-200 rounded-lg p-4" data-testid="pqc-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label htmlFor="pqc-date-from" className="text-xs text-slate-600">GIN Date From</Label>
            <Input id="pqc-date-from" type="date" value={form.date_from} onChange={(e) => set("date_from")(e.target.value)} className="h-9 mt-1" data-testid="pqc-date-from" />
          </div>
          <div>
            <Label htmlFor="pqc-date-to" className="text-xs text-slate-600">GIN Date To</Label>
            <Input id="pqc-date-to" type="date" value={form.date_to} onChange={(e) => set("date_to")(e.target.value)} className="h-9 mt-1" data-testid="pqc-date-to" />
          </div>
          <div>
            <Label htmlFor="pqc-gin-no" className="text-xs text-slate-600">GIN Number</Label>
            <Input id="pqc-gin-no" value={form.gin_no} onChange={(e) => set("gin_no")(e.target.value)} placeholder="e.g. GIN-1001" className="h-9 mt-1" data-testid="pqc-gin-no" />
          </div>
          <div>
            <Label htmlFor="pqc-supplier" className="text-xs text-slate-600">Supplier / Vendor</Label>
            <Input id="pqc-supplier" list="pqc-supplier-options" value={form.supplier} onChange={(e) => set("supplier")(e.target.value)} placeholder="Supplier or vendor" className="h-9 mt-1" data-testid="pqc-supplier" />
            <datalist id="pqc-supplier-options">
              {suppliers.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div>
            <Label htmlFor="pqc-item" className="text-xs text-slate-600">Item</Label>
            <Input id="pqc-item" value={form.item} onChange={(e) => set("item")(e.target.value)} placeholder="Item code or name" className="h-9 mt-1" data-testid="pqc-item" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Source</Label>
            <Select value={form.source} onValueChange={set("source")}>
              <SelectTrigger className="h-9 mt-1" data-testid="pqc-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                {SOURCE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="pqc-search" className="text-xs text-slate-600">Search</Label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="pqc-search" value={form.search} onChange={(e) => set("search")(e.target.value)} placeholder="Any GIN / PO / DC no., supplier, invoice, vehicle or item" className="h-9 pl-8" data-testid="pqc-search" />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 mt-4">
          <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="pqc-search-btn">
            <Search className="w-3.5 h-3.5 mr-1.5" /> Search
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={reset} data-testid="pqc-reset-btn">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={doExport} disabled={exporting} data-testid="pqc-export-excel">
            {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />}
            Export to Excel
          </Button>
        </div>
      </form>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="pqc-summary">
        {SUMMARY.map(({ key, label, icon: Icon, tone }) => (
          <div key={key} className="kpi-card !p-4" data-testid={`pqc-summary-${key}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 leading-tight">{label}</div>
                <div className="mt-1.5 text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
                  {fmtQty(data?.summary?.[key] ?? 0)}
                </div>
              </div>
              <div className={`w-8 h-8 shrink-0 rounded-md flex items-center justify-center ${tone}`}>
                <Icon className="w-4 h-4" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 text-rose-700 text-sm px-3 py-2">{error}</div>
      )}

      <div className="relative border border-slate-200 rounded-lg bg-white overflow-x-auto scroll-thin" data-testid="pqc-table-wrap">
        {loading && !data && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-20">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        )}
        <table className="min-w-full text-xs" data-testid="pqc-table">
          <thead className="bg-slate-50">
            <tr>
              {PQC_COLUMNS.map((col) => (
                <SortHeader key={col.field} col={col} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50/70 align-top" data-testid={`pqc-row-${row.id}`}>
                {PQC_COLUMNS.map((col) => (
                  <td key={col.field} className={`px-3 py-2 whitespace-nowrap text-slate-700 ${col.kind === "number" ? "text-right tabular-nums" : ""}`}>
                    {renderCell(row, col)}
                  </td>
                ))}
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={PQC_COLUMNS.length} className="text-center py-16 text-slate-400">
                  No GINs are pending QC for the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
        <div data-testid="pqc-row-count">
          Showing {rows.length === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total} GIN{total === 1 ? "" : "s"}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500">Rows per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger className="w-20 h-8 text-xs" data-testid="pqc-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page <= 1} onClick={() => setPage(1)} data-testid="pqc-page-first">«</Button>
            <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="pqc-page-prev">‹</Button>
            <span className="text-xs px-2">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="pqc-page-next">›</Button>
            <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page >= totalPages} onClick={() => setPage(totalPages)} data-testid="pqc-page-last">»</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
